<template>
  <div class="study-container">
    <div class="study-details">
      <popover-timestamp
        :value="startTimestamp"
        :label="$t('widget.currentReadings.studyStart')"
        :invalidText="$t('map.popover.valueBlank')"
        customClass="small-subtitle"
        :tooltipText="startTimestampTooltipText"
        dataCy="studyStart"
      />
      <popover-timestamp
        :value="stopTimestamp"
        :label="$t('widget.currentReadings.studyStop')"
        :invalidText="$t('map.popover.valueBlank')"
        customClass="small-subtitle"
        dataCy="studyStop"
      />
    </div>
    <one-measurement-monitoring
      :deviceId="deviceId"
      :selectedParameter="selectedParameter"
      :readings="readings"
      size="medium"
    />
    <measurement-statistics
      :deviceId="deviceId"
      :measurementStatisticsReadings="measurementStatisticsReadings"
      :selectedParameter="selectedParameter"
    />
    <device-conditions
      :deviceId="deviceId"
      :lastTelemetryTimestamp="lastTelemetryTimestamp"
    />
  </div>
</template>
<script>
import MeasurementStatistics from '@/components/widgets/currentReadings/content/MeasurementStatistics.vue'
import OneMeasurementMonitoring from '@/components/devicePopover/layouts/OneMeasurementMonitoring.vue'
import popoverTimestamp from '@/components/devicePopover/content/popoverTimestamp.vue'
import DeviceConditions from '@/components/widgets/currentReadings/content/DeviceConditions.vue'
import { getStatisticsReadingsFromState } from '@/helpers/studies/statisticsHelper'
import {
  getTelemetryReadingsObject,
  getAllTelemetryStatisticsReadingsObject,
} from '@/helpers/devices/telemetryHelper'
import { getLatestStudyTelemetry } from '@/helpers/studies/telemetryHelper'
import { getLatestStudyState } from '@/helpers/studies/stateHelper'
import { utils } from '@fusion/saas-components'

// constants
const terminationReasons = utils.constants.study.terminationReasons

export default {
  name: 'StudyContent',
  components: {
    'measurement-statistics': MeasurementStatistics,
    'one-measurement-monitoring': OneMeasurementMonitoring,
    'popover-timestamp': popoverTimestamp,
    'device-conditions': DeviceConditions,
  },
  props: {
    deviceId: String,
    latestDeviceStudy: Object,
    selectedParameter: Number,
  },
  computed: {
    latestStudyTelemetry() {
      return getLatestStudyTelemetry(this.latestDeviceStudy)
    },
    lastTelemetryTimestamp() {
      return this.latestStudyTelemetry?.timestamp || null
    },
    latestStudyState() {
      return getLatestStudyState(this.latestDeviceStudy)
    },
    readings() {
      return getTelemetryReadingsObject(this.latestStudyTelemetry)
    },
    measurementStatisticsReadings() {
      return (
        // Live studies have statistics in telemetry, while offline studies in state.
        getAllTelemetryStatisticsReadingsObject(this.latestStudyTelemetry) ||
        getStatisticsReadingsFromState(
          this.latestStudyState,
          this.lastTelemetryTimestamp
        )
      )
    },
    wasDeviceConnectedAfterStart() {
      return (
        this.latestDeviceStudy &&
        this.latestDeviceStudy.upload_complete &&
        this.latestDeviceStudy.termination_reason ===
          terminationReasons.CONNECTED_AFTER_START
      )
    },
    startTimestamp() {
      if (
        !this.latestDeviceStudy?.min_timestamp ||
        this.wasDeviceConnectedAfterStart
      ) {
        return null
      }

      return this.latestDeviceStudy.min_timestamp
    },
    startTimestampTooltipText() {
      return this.wasDeviceConnectedAfterStart
        ? this.$t('widget.currentReadings.studyStartConnectedAfterStart')
        : ''
    },
    stopTimestamp() {
      if (
        this.latestDeviceStudy &&
        this.latestDeviceStudy.upload_complete &&
        this.latestDeviceStudy.termination_reason ===
          terminationReasons.COMPLETE
      ) {
        return this.latestDeviceStudy.max_timestamp
      }

      return null
    },
  },
}
</script>

<style lang="scss">
div[data-cy='studyWidget'] {
  .device-conditions-wrapper {
    right: 4%;
  }
}
</style>

<style lang="scss" scoped>
.study-details {
  display: flex;
  justify-content: space-between;
}
</style>
